import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from '@produzionejb/ui';
import { useAuthStore } from '../../stores/auth-store';
import { acceptInvite, fetchInvitePeek, type InvitePeekResponse } from '../../lib/users-api';

function isStrong(pwd: string): { ok: boolean; reasons: string[] } {
  const reasons: string[] = [];
  if (pwd.length < 12) reasons.push('almeno 12 caratteri');
  if (!/[A-Z]/.test(pwd)) reasons.push('una maiuscola');
  if (!/[a-z]/.test(pwd)) reasons.push('una minuscola');
  if (!/[0-9]/.test(pwd)) reasons.push('un numero');
  if (!/[^A-Za-z0-9]/.test(pwd)) reasons.push('un simbolo');
  return { ok: reasons.length === 0, reasons };
}

export function AcceptInvitePage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);

  const [info, setInfo] = useState<InvitePeekResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setTokenError(t('acceptInvite.invalidToken'));
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchInvitePeek(token);
        if (!cancelled) setInfo(data);
      } catch {
        if (!cancelled) setTokenError(t('acceptInvite.invalidToken'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, t]);

  const strong = isStrong(password);
  const mismatch = confirm.length > 0 && password !== confirm;
  const canSubmit = !!info && strong.ok && password === confirm && !submitting;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token || !canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await acceptInvite(token, password);
      setAuth({ accessToken: res.accessToken, user: res.user });
      navigate('/', { replace: true });
    } catch (err) {
      setSubmitError((err as Error).message);
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('acceptInvite.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading && <p className="text-sm text-slate-500">{t('common.loading')}</p>}

          {tokenError && (
            <div className="space-y-3">
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                {tokenError}
              </div>
              <p className="text-sm text-slate-600">{t('acceptInvite.invalidHint')}</p>
              <Link to="/login" className="inline-block text-sm font-medium text-brand-700 hover:underline">
                {t('acceptInvite.goLogin')}
              </Link>
            </div>
          )}

          {!loading && info && !tokenError && (
            <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                <p>{t('acceptInvite.welcome', { name: info.name ?? info.email })}</p>
                <p className="text-xs text-slate-500 mt-1">
                  {t('acceptInvite.account', { email: info.email })}
                </p>
                <p className="text-xs text-slate-500">
                  {t('acceptInvite.expiresAt', { date: new Date(info.expiresAt).toLocaleString('it-IT') })}
                </p>
              </div>

              <div className="space-y-1">
                <Label>{t('acceptInvite.password')} *</Label>
                <Input
                  type="password"
                  required
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                {password.length > 0 && !strong.ok && (
                  <p className="text-xs text-amber-700">
                    {t('acceptInvite.passwordRequires', { reasons: strong.reasons.join(', ') })}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <Label>{t('acceptInvite.confirmPassword')} *</Label>
                <Input
                  type="password"
                  required
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
                {mismatch && <p className="text-xs text-red-700">{t('acceptInvite.passwordMismatch')}</p>}
              </div>

              {submitError && (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                  {submitError}
                </div>
              )}

              <Button type="submit" disabled={!canSubmit} className="w-full">
                {submitting ? t('common.loading') : t('acceptInvite.submit')}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
